/**
 * R31 avatar Phase-1 — the `?avatar=1` feature flag (design-language §6 / project-root
 * docs/avatar-plan.md Phase 1).
 *
 * The 3D avatar is OFF by default. It is enabled per-visit by the `?avatar=1` query param,
 * exactly the same inert-in-production shape as the `?audiodebug=1` instrumentation
 * (`useVoice.ts` RC-3): a single URL read, mirrored to a module-level constant so the rest of
 * the app can branch on it synchronously without re-reading `location` or threading a prop.
 *
 * Why a query param (not a store field): the avatar is an APP-CONFIG choice for a whole visit,
 * not per-session voice state. Keeping it out of the Zustand session store means a
 * `reset()` / new-conversation never flips it, and the VoiceOrb path stays byte-for-byte
 * unchanged when the flag is absent.
 *
 * Hard guarantee when the flag is OFF: `avatarEnabled()` returns false, the caller renders the
 * existing <VoiceOrb>, and NO Three.js / TalkingHead code is ever imported (Avatar3D is a
 * `lazy()` chunk mounted only behind this flag) — so the initial bundle is identical to today.
 *
 * SSR/no-window-safe: guards `typeof window`. Evaluated once at module load (the flag can't
 * change mid-visit without a reload, matching the audiodebug precedent).
 */

function readAvatarFlag(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const v = params.get('avatar');
    // `?avatar`, `?avatar=1`, `?avatar=true`, `?avatar=on` all enable; `?avatar=0` disables.
    if (v === null) return false;
    return v === '' || v === '1' || v === 'true' || v === 'on';
  } catch {
    return false;
  }
}

/** True when `?avatar=1` (or an equivalent truthy value) is present in the URL. Read once at load. */
const AVATAR_FLAG = readAvatarFlag();

/**
 * Whether the 3D avatar is enabled for this visit. When false the caller MUST render the
 * existing <VoiceOrb> and MUST NOT import any avatar/Three.js code.
 */
export function avatarEnabled(): boolean {
  return AVATAR_FLAG;
}
